import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { toast } from 'react-toastify';
import {
  LOGOUT_USER,
  SET_USEREFRESH_TOKEN,
  selectAuth,
  selectAuthToken,
} from '~/store/feature/Auth';
import { RootState } from '~/store/index';
import { REFRESHTOKEN_PATH } from './endpoints';

const tokenExpired = 'Token expired.';
const tokenSuccess = 'Successfully re-authenticated';
const tokenFail = 'Token failed';

const mutex = new Mutex();

export const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL + '/',
  prepareHeaders: (headers, { getState }) => {
    const token = selectAuthToken(getState() as RootState);

    if (token?.access) headers.set('Authorization', `Bearer ${token.access}`);
    return headers;
  },
  credentials: 'include',
});

interface RefreshTokenProp {
  access?: string;
}

interface CustomFetchArgs extends FetchArgs {
  skipLogout?: boolean;
}

interface ErrorResponse {
  message?: string;
}

export const baseQueryWithReauth: BaseQueryFn<
  string | CustomFetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (!result.error) return result;

  const errorData = result.error.data as ErrorResponse;

  // Handle 400 errors
  if (result.error.status === 400 && errorData?.message) {
    toast.error(errorData.message);
    return result;
  }

  // Handle 401 errors
  if (result.error.status === 401) {
    return handle401(args, api, extraOptions, result);
  }

  return result;
};

// Extracted function to handle 401 and refresh token
async function handle401(
  args: string | CustomFetchArgs,
  api: any,
  extraOptions: any,
  result: any
) {
  if (mutex.isLocked()) {
    await mutex.waitForUnlock();
    return baseQuery(args, api, extraOptions);
  }

  const release = await mutex.acquire();

  try {
    const loadingNotification = toast.warning(tokenExpired);
    const { user } = selectAuth(api.getState() as RootState);

    const refreshResult = await baseQuery(
      {
        url: REFRESHTOKEN_PATH,
        method: 'POST',
        body: { refresh: user?.token?.refresh },
      },
      api,
      extraOptions
    );

    const resData = refreshResult.data as RefreshTokenProp;

    if (resData?.access) {
      toast.update(loadingNotification, {
        render: tokenSuccess,
        type: 'success',
        autoClose: 2000,
      });
      api.dispatch(SET_USEREFRESH_TOKEN(resData.access));
      return baseQuery(args, api, extraOptions);
    } else {
      toast.update(loadingNotification, {
        render: tokenFail,
        type: 'error',
        autoClose: 2000,
      });
      if (typeof args !== 'string' && !args.skipLogout) {
        api.dispatch(LOGOUT_USER());
      }
      return result;
    }
  } finally {
    release();
  }
}

